// Quality panel: the voice's live tier (#32, ADR 0003, docs/perf/live.md).
//
// It offers "Auto" and each tier of the source module's quality table, and
// keeps the choice in the view state's quality ("auto" or a tier id). In auto,
// the load monitor reads the running voice's telemetry and the quality
// controller steps the tier down when the computer falls behind and back up
// when there is room; the panel sends each decision to the voice as its
// quality control, which crossfades to the new tier. It also shows the tier
// playing, the load and the dropouts.

import { LoadMonitor, type LoadSample } from "../../../audio/features/load-monitor";
import { AUTO, QualityController, qualityTable } from "../../../audio/features/quality-controller";
import { defineFeature } from "../../../registry";
import { el } from "../../controls/dom";
import { strings } from "./strings";

export default defineFeature({
  id: "quality",
  title: strings.title,
  slot: "panel",
  order: 30,
  mount({ root, state, audio }) {
    const table = qualityTable(audio.source);
    if (table === null || table.tiers.length < 2) {
      root.append(el("p", { class: "panel-note" }, strings.none));
      return;
    }
    const controller = new QualityController(table, state.state.quality);

    const fieldset = el("fieldset", { class: "panel-choices" }, el("legend", {}, strings.legend));
    const radios: [string, HTMLInputElement][] = [];
    const choice = (value: string, label: string, hint: string): void => {
      const input = el("input", { type: "radio", name: "quality", value, "data-quality": value });
      input.addEventListener("change", () => {
        if (input.checked) state.set({ quality: value });
      });
      radios.push([value, input]);
      fieldset.append(el("label", { title: hint }, input, el("span", {}, label)));
    };
    choice(AUTO, strings.auto, strings.autoHint);
    for (const t of table.tiers) {
      if (t.kind === "loop-set") continue; // P5.5
      const label = t.kind === "full" ? `${strings.tier(t.id)} (${strings.offline})` : strings.tier(t.id);
      choice(t.id, label, t.description);
    }

    const playing = el("dd", { "data-quality-tier": "" });
    const load = el("dd", { "data-quality-load": "" });
    const misses = el("dd", { "data-quality-misses": "" });
    const last = el("dd", { "data-quality-last": "" });
    const status = el(
      "dl",
      {},
      el("dt", {}, strings.playing),
      playing,
      el("dt", {}, strings.load),
      load,
      el("dt", {}, strings.misses),
      misses,
      el("dt", {}, strings.last),
      last,
    );
    const off = el("p", { class: "panel-note" }, strings.off);
    root.append(fieldset, status, off);

    // A voice that starts later starts at the controller's tier.
    const previous = audio.startQuality;
    let startTier = controller.defaultTier;
    audio.startQuality = () => {
      startTier = controller.tier;
      return startTier === controller.defaultTier ? null : startTier;
    };

    let missTotal = 0;
    let missSource: "underruns" | "stalls" = "stalls";
    const show = (): void => {
      for (const [value, input] of radios) input.checked = value === controller.choice;
      const on = audio.status === "on";
      status.hidden = !on;
      off.hidden = on;
      playing.textContent = `${strings.tier(controller.tierId)}${controller.auto ? ` (${strings.auto.toLowerCase()})` : ""}`;
      const sample = controller.lastSample;
      load.textContent = sample === null ? strings.measuring : strings.loadValue(100 * sample.load);
      misses.textContent = strings.missesValue(missTotal, missSource);
      const h = controller.history[controller.history.length - 1]!;
      last.textContent = strings.change(h.reason, strings.tier(table.tiers[h.to]!.id), h.time, h.load);
    };

    const send = (tier: number | null): void => {
      if (tier !== null && audio.status === "on" && audio.accepts("quality")) audio.control("quality", tier);
    };

    let detach: (() => void)[] = [];
    const onSample = (sample: LoadSample): void => {
      missTotal += sample.misses;
      if (sample.underruns !== null) missSource = "underruns";
      send(controller.update(sample));
      show();
    };
    const onStatus = (): void => {
      for (const d of detach) d();
      detach = [];
      const engine = audio.engine;
      if (engine !== null) {
        const monitor = new LoadMonitor();
        missTotal = 0;
        detach.push(monitor.attach(engine, onSample));
        // A voice restarted after an error starts at its start tier again.
        detach.push(
          engine.onReady(() => {
            monitor.reset();
            if (startTier !== controller.tier) send(controller.tier);
          }),
        );
      }
      show();
    };
    const onState = (): void => {
      if (state.state.quality !== controller.choice) send(controller.choose(state.state.quality));
      show();
    };

    const offs = [state.subscribe(onState), audio.onStatus(onStatus)];
    onStatus();
    return () => {
      for (const o of offs) o();
      for (const d of detach) d();
      audio.startQuality = previous;
    };
  },
});
